<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import type {
    PermissionLevel,
    PermissionRowConfig,
    PermissionSectionConfig,
  } from "../../_logic/permission-sections"

  type PermissionKey = string
  type Row = PermissionRowConfig<PermissionKey>

  type Props = {
    sections?: PermissionSectionConfig<PermissionKey>[]
    levels?: Record<PermissionKey, PermissionLevel>
    scopes?: Record<PermissionKey, string | null>
    levelOptionsForRow?: (row: Row) => Row["levelOptions"]
    scopeOptionsForRow?: (row: Row) => NonNullable<Row["scopeOptions"]>
    onLevelChange?: (key: PermissionKey, level: PermissionLevel) => void
    onScopeChange?: (key: PermissionKey, scope: string | null) => void
    isLevelDisabled?: (row: Row) => boolean
    isScopeDisabled?: (row: Row) => boolean
    showSectionHeadings?: boolean
    invertColors?: boolean
    brightInputs?: boolean
    /** Shows the grants as text; no input is rendered and no change can be emitted. */
    readonly?: boolean
  }

  let {
    sections = [],
    levels = {},
    scopes = {},
    levelOptionsForRow = (row: Row) => row.levelOptions ?? [],
    scopeOptionsForRow = (row: Row) => row.scopeOptions ?? [],
    onLevelChange = () => {},
    onScopeChange = () => {},
    isLevelDisabled = () => false,
    isScopeDisabled = () => false,
    showSectionHeadings = true,
    invertColors = false,
    brightInputs = false,
    readonly: readOnly = false,
  }: Props = $props()

  const levelOf = (row: Row): PermissionLevel => levels[row.key] ?? "none"

  const levelName = (row: Row): string =>
    row.levelOptions.find((option) => option.value === levelOf(row))?.name ?? "Kein Zugriff"

  const scopeName = (row: Row): string | null => {
    if (levelOf(row) === "none" || !row.scopeOptions?.length) return null

    return row.scopeOptions.find((option) => option.value === scopes[row.key])?.name ?? null
  }
</script>

<div class="permissions-grid" class:inverted={invertColors}>
  {#each sections as section (section.id)}
    <ActionBox label={showSectionHeadings ? section.title : ""} darkMode={!invertColors}>
      {#snippet children()}
        {#if readOnly}
          <dl class="section-body readonly">
            {#each section.rows as row (row.key)}
              {@const scope = scopeName(row)}
              <div class="readonly-row">
                <dt>{row.label}</dt>
                <dd>
                  {levelName(row)}{#if scope}{" · "}<span class="scope">{scope}</span>{/if}
                </dd>
              </div>
            {/each}
          </dl>
        {:else}
          <div class="section-body">
            {#each section.rows as row (row.key)}
              <div class="perm-row">
                <Input
                  type="select"
                  placeholder={row.label}
                  options={levelOptionsForRow(row)}
                  value={levels[row.key] ?? "none"}
                  tooltip={row.levelTooltip ?? row.description}
                  clearable={false}
                  onValueChange={(value) => {
                    if (value) onLevelChange(row.key, value as PermissionLevel)
                  }}
                  disabled={isLevelDisabled(row)}
                  brightBackground={invertColors || brightInputs}
                />
                {#if row.scopeOptions?.length}
                  <Input
                    type="select"
                    placeholder="{row.label} Umfang"
                    options={scopeOptionsForRow(row)}
                    value={scopes[row.key] ?? null}
                    tooltip={row.scopeTooltip ?? row.description}
                    clearable={false}
                    onValueChange={(value) => {
                      if (value) onScopeChange(row.key, String(value))
                    }}
                    disabled={isScopeDisabled(row)}
                    brightBackground={invertColors || brightInputs}
                  />
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      {/snippet}
    </ActionBox>
  {/each}
</div>

<style lang="less">
  .permissions-grid {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 0;
  }

  .section-body {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-top: 0.6rem;
    min-width: 0;
  }

  .perm-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 0.6rem;
    align-items: center;
  }

  .readonly {
    gap: 0;
    color: var(--text-invers-100);
  }

  .readonly-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.25rem 1rem;
    padding: 0.45rem 0;
    border-bottom: 1px solid color-mix(in srgb, var(--text-invers-100) 12%, transparent);

    &:last-child {
      border-bottom: none;
    }

    dt {
      font-weight: 600;
      overflow-wrap: anywhere;
    }

    dd {
      margin: 0;
      overflow-wrap: anywhere;
    }
  }

  .scope {
    color: color-mix(in srgb, var(--text-invers-100) 70%, transparent);
  }

  .inverted .readonly {
    color: var(--neutral-white, #fff);
  }

  .inverted .readonly-row {
    border-bottom-color: color-mix(in srgb, var(--neutral-white, #fff) 20%, transparent);
  }

  .inverted .scope {
    color: color-mix(in srgb, var(--neutral-white, #fff) 75%, transparent);
  }
</style>
